import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import 'mapbox-gl/dist/mapbox-gl.css';

const MAPBOX_TOKEN = 'pk.eyJ1Ijoiam9zaGhvb3ZlciIsImEiOiJjbWJmd3Q2bmwyNW5wMmtwdnF0d200enpvIn0.qCUbYphDsIt1fXpNb1ISZA';

const MapboxParcelDrawer = ({ 
  onParcelDrawn, 
  onSetbacksUpdated,
  showSetbacks: externalShowSetbacks,
  lightMode = true 
}) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const draw = useRef(null);
  const [parcelCoordinates, setParcelCoordinates] = useState(null);
  const [setbacks, setSetbacks] = useState(null);
  const [showSetbacks, setShowSetbacks] = useState(false);
  const [copied, setCopied] = useState(false);
  const [setbackDistances, setSetbackDistances] = useState([]);
  const [setAllSetbacks, setSetAllSetbacks] = useState(false);
  const [setbackValue, setSetbackValue] = useState('');
  const [setbackLabels, setSetbackLabels] = useState([]);
  const [parcelId, setParcelId] = useState('');
  const [parcelSearchLoading, setParcelSearchLoading] = useState(false);
  const [parcelInfo, setParcelInfo] = useState(null);

  // Add setback dialog component
  const SetbackDialog = () => {
    return (
      <div style={{
        position: 'absolute',
        top: '80px',
        right: '60px',
        width: '280px',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        maxHeight: 'calc(100vh - 160px)', // Ensure it fits in viewport
        border: '1px solid #E5E7EB'
      }}>
        {/* Header - Fixed */}
        <div style={{
          padding: '16px',
          borderBottom: '1px solid #E5E7EB',
          flexShrink: 0
        }}>
          <h3 style={{ 
            fontSize: '16px', 
            fontWeight: '600', 
            margin: 0 
          }}>Setback Settings</h3>
        </div>

        {/* Scrollable Content */}
        <div style={{
          padding: '16px',
          overflowY: 'auto',
          flexGrow: 1
        }}>
          {/* Set All Setbacks Section */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              marginBottom: '12px'
            }}>
              <input
                type="checkbox"
                id="setAllSetbacks"
                checked={setAllSetbacks}
                onChange={(e) => {
                  setSetAllSetbacks(e.target.checked);
                  if (e.target.checked) {
                    const value = setbackDistances[0] || 30;
                    const newDistances = Array(setbackDistances.length).fill(value);
                    setSetbackValue(value.toString());
                    setSetbackDistances(newDistances);
                    calculateSetbacks(parcelCoordinates, newDistances);
                  }
                }}
                style={{ cursor: 'pointer' }}
              />
              <label htmlFor="setAllSetbacks" style={{ fontSize: '14px', cursor: 'pointer' }}>
                Set all setbacks to same value
              </label>
            </div>

            {setAllSetbacks && (
              <div style={{ 
                padding: '12px',
                backgroundColor: '#f3f4f6',
                borderRadius: '4px'
              }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>
                  Value for all setbacks (ft)
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={setbackValue}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSetbackValue(value);
                    const numValue = Number(value) || 0;
                    const newDistances = Array(setbackDistances.length).fill(numValue);
                    setSetbackDistances(newDistances);
                    calculateSetbacks(parcelCoordinates, newDistances);
                  }}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '4px',
                    backgroundColor: 'white'
                  }}
                />
              </div>
            )}
          </div>

          {/* Individual Setback Fields */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {setbackDistances.map((distance, index) => (
              <div key={index}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>
                  {setbackLabels[index] || `Side ${index + 1}`} (ft)
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={distance || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    const numValue = Number(value) || 0;
                    const newDistances = [...setbackDistances];
                    newDistances[index] = numValue;
                    setSetbackDistances(newDistances);
                    calculateSetbacks(parcelCoordinates, newDistances);
                  }}
                  disabled={setAllSetbacks}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '4px',
                    backgroundColor: setAllSetbacks ? '#f3f4f6' : 'white'
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (!map.current && mapContainer.current) {
      mapboxgl.accessToken = MAPBOX_TOKEN;
      
      // Initialize map
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: lightMode ? 'mapbox://styles/mapbox/light-v11' : 'mapbox://styles/mapbox/dark-v11',
        center: [-95.7129, 37.0902], // Center of US
        zoom: 3
      });

      // Initialize drawing tools first
      draw.current = new MapboxDraw({
        displayControlsDefault: false,
        controls: {
          polygon: true,
          trash: true
        },
        styles: [
          // Vertex points
          {
            'id': 'gl-draw-point',
            'type': 'circle',
            'filter': ['all',
              ['==', '$type', 'Point'],
              ['==', 'meta', 'vertex']
            ],
            'paint': {
              'circle-radius': 6,
              'circle-color': '#fff',
              'circle-stroke-color': '#3B82F6',
              'circle-stroke-width': 2
            }
          },
          // Vertex points when dragging/moving
          {
            'id': 'gl-draw-point-active',
            'type': 'circle',
            'filter': ['all',
              ['==', '$type', 'Point'],
              ['==', 'meta', 'vertex'],
              ['==', 'active', 'true']
            ],
            'paint': {
              'circle-radius': 7,
              'circle-color': '#fff',
              'circle-stroke-color': '#3B82F6',
              'circle-stroke-width': 3
            }
          },
          // Mid points between vertices
          {
            'id': 'gl-draw-mid-point',
            'type': 'circle',
            'filter': ['all',
              ['==', '$type', 'Point'],
              ['==', 'meta', 'midpoint']
            ],
            'paint': {
              'circle-radius': 4,
              'circle-color': '#fff',
              'circle-stroke-color': '#3B82F6',
              'circle-stroke-width': 2
            }
          },
          // Polygon fill
          {
            'id': 'gl-draw-polygon-fill',
            'type': 'fill',
            'filter': ['all', ['==', '$type', 'Polygon']],
            'paint': {
              'fill-color': '#3B82F6',
              'fill-outline-color': '#3B82F6',
              'fill-opacity': 0.2
            }
          },
          // Polygon outline when inactive
          {
            'id': 'gl-draw-polygon-stroke',
            'type': 'line',
            'filter': ['all',
              ['==', '$type', 'Polygon'],
              ['!=', 'mode', 'static']
            ],
            'paint': {
              'line-color': '#3B82F6',
              'line-width': 2,
              'line-dasharray': [2, 2]
            }
          },
          // Polygon outline when active
          {
            'id': 'gl-draw-polygon-stroke-active',
            'type': 'line',
            'filter': ['all',
              ['==', '$type', 'Polygon'],
              ['==', 'active', 'true']
            ],
            'paint': {
              'line-color': '#3B82F6',
              'line-width': 3,
              'line-dasharray': [0, 0]
            }
          }
        ]
      });

      // Add search control
      const geocoder = new MapboxGeocoder({
        accessToken: MAPBOX_TOKEN,
        mapboxgl: mapboxgl,
        marker: false,
        types: 'country,region,postcode,district,place,locality,neighborhood,address,poi,poi.landmark',
        countries: 'us',
        bbox: [-125.0, 24.396308, -66.93457, 49.384358], // Limit to continental US
        proximity: {
          longitude: -95.7129,
          latitude: 37.0902
        },
        minLength: 3,
        fuzzyMatch: true,
        zoom: 16 // Zoom in closer when selecting a location
      });
      
      // Add controls in specific order and position
      map.current.addControl(geocoder, 'top-left');
      map.current.addControl(draw.current, 'top-right');
      map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

      // Add event listener for geocoder result selection
      geocoder.on('result', () => {
        // Focus the map container after a short delay to ensure the geocoder is done processing
        setTimeout(() => {
          if (mapContainer.current) {
            mapContainer.current.focus();
          }
        }, 100);
      });

      // Style the geocoder container to accommodate our buttons
      const geocoderContainer = document.querySelector('.mapboxgl-ctrl-geocoder');
      if (geocoderContainer) {
        geocoderContainer.style.width = 'auto';
        geocoderContainer.style.minWidth = '300px';
        geocoderContainer.style.padding = '8px';
        geocoderContainer.style.height = '52px'; // 36px height + 8px padding top/bottom
        
        // Style the input to match button height
        const searchInput = geocoderContainer.querySelector('input');
        if (searchInput) {
          searchInput.style.height = '36px';
          searchInput.style.padding = '6px 12px 6px 24px';
        }

        // Center the search icon
        const searchIcon = geocoderContainer.querySelector('.mapboxgl-ctrl-geocoder--icon');
        if (searchIcon) {
          searchIcon.style.top = '50%';
          searchIcon.style.transform = 'translateY(-50%)';
          searchIcon.style.marginRight = '4px';
        }
      }

      // Handle drawing events
      map.current.on('draw.create', handleDrawCreate);
      map.current.on('draw.update', handleDrawUpdate);
      map.current.on('draw.delete', handleDrawDelete);
    }

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [lightMode]);

  // Update showSetbacks when externalShowSetbacks changes
  useEffect(() => {
    setShowSetbacks(externalShowSetbacks);
    if (externalShowSetbacks && parcelCoordinates) {
      // Initialize setbacks if they haven't been set
      if (setbackDistances.length === 0) {
        const sideCount = parcelCoordinates.length - 1;
        const initialDistances = Array(sideCount).fill(30);
        setSetbackDistances(initialDistances);
        calculateSetbacks(parcelCoordinates, initialDistances);
      } else {
        calculateSetbacks(parcelCoordinates, setbackDistances);
      }
    }
  }, [externalShowSetbacks, parcelCoordinates]);

  // Handle parcel drawing
  const handleDrawCreate = (e) => {
    const coordinates = e.features[0].geometry.coordinates[0];
    console.log('Draw Create - Coordinates:', coordinates);
    setParcelCoordinates(coordinates);
    
    // Initialize setback distances with default values for each side
    const sideCount = coordinates.length - 1; // Last point connects to first
    const initialDistances = Array(sideCount).fill(30); // Default 30ft setbacks
    console.log('Draw Create - Initial distances:', initialDistances);
    
    setSetbackDistances(initialDistances);
    calculateSetbacks(coordinates, initialDistances);
    
    if (onParcelDrawn) {
      onParcelDrawn(coordinates);
    }
  };

  const handleDrawUpdate = (e) => {
    const coordinates = e.features[0].geometry.coordinates[0];
    setParcelCoordinates(coordinates);
    
    // Reinitialize setbacks if number of sides changed
    const sideCount = coordinates.length - 1;
    if (setbackDistances.length !== sideCount) {
      const newDistances = Array(sideCount).fill(30);
      setSetbackDistances(newDistances);
      if (showSetbacks) {
        calculateSetbacks(coordinates, newDistances);
      }
    } else if (showSetbacks) {
      calculateSetbacks(coordinates, setbackDistances);
    }
    
    if (onParcelDrawn) {
      onParcelDrawn(coordinates);
    }
  };

  const handleDrawDelete = () => {
    setParcelCoordinates(null);
    setSetbacks(null);
    setSetbackDistances([]);
    setSetbackValue('');
    setSetAllSetbacks(false);
    
    if (onParcelDrawn) {
      onParcelDrawn(null);
    }
    if (onSetbacksUpdated) {
      onSetbacksUpdated(null);
    }
  };

  // Calculate setbacks for each side of the parcel
  const calculateSetbacks = (coordinates, distances = null) => {
    if (!coordinates || coordinates.length < 4) {
      console.log('Invalid coordinates for setback calculation:', coordinates);
      return;
    }

    const sideCount = coordinates.length - 1;
    const defaultDistance = 30; // Default setback distance in feet
    const setbackPoints = [];
    const newSetbackDistances = distances || Array(sideCount).fill(defaultDistance);
    const newSetbackLabels = [];
    
    console.log('Calculating setbacks:', {
      sideCount,
      coordinates,
      distances: newSetbackDistances
    });
    
    // Calculate setback points for each side
    for (let i = 0; i < sideCount; i++) {
      const start = coordinates[i];
      const end = coordinates[i + 1];
      
      // Calculate direction of the side
      const dx = end[0] - start[0];
      const dy = end[1] - start[1];
      const length = Math.sqrt(dx * dx + dy * dy);
      
      // Calculate angle in degrees (0° is East, 90° is North)
      let angle = Math.atan2(dy, dx) * 180 / Math.PI;
      if (angle < 0) angle += 360;
      
      // Generate label based on predominant direction
      let label = `Side ${i + 1}`;
      newSetbackLabels[i] = label;
      
      // Calculate perpendicular vectors for setbacks
      const setbackDistance = newSetbackDistances[i] * 0.0000033; // Convert feet to approx degrees
      const perpX = dy / length * setbackDistance;
      const perpY = -dx / length * setbackDistance;
      
      // Add setback points
      setbackPoints.push([
        [start[0] + perpX, start[1] + perpY],
        [end[0] + perpX, end[1] + perpY]
      ]);
    }

    console.log('Setback calculation results:', {
      setbackPoints,
      distances: newSetbackDistances,
      labels: newSetbackLabels
    });

    setSetbacks(setbackPoints);
    setSetbackDistances(newSetbackDistances);
    setSetbackLabels(newSetbackLabels);
    
    if (onSetbacksUpdated) {
      onSetbacksUpdated(setbackPoints);
    }
  };

  // Handle copying coordinates
  const copyToVisualizer = () => {
    if (parcelCoordinates && setbacks) {
      // Calculate bounds
      let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
      parcelCoordinates.forEach(coord => {
        minX = Math.min(minX, coord[0]);
        maxX = Math.max(maxX, coord[0]);
        minY = Math.min(minY, coord[1]);
        maxY = Math.max(maxY, coord[1]);
      });

      // Convert to feet (approximate conversion from degrees)
      const feetPerDegree = 364000;
      const width = Math.round((maxX - minX) * feetPerDegree);
      const height = Math.round((maxY - minY) * feetPerDegree);

      // Calculate setback distances
      const setbackDistances = setbacks.map(setback => {
        const [start, end] = setback;
        return Math.round(Math.sqrt(
          Math.pow(end[0] - start[0], 2) + Math.pow(end[1] - start[1], 2)
        ) * feetPerDegree);
      });

      onParcelDrawn(parcelCoordinates);
      onSetbacksUpdated(setbacks);

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Update setback visibility
  useEffect(() => {
    if (!map.current || !setbacks || !parcelCoordinates) {
      return;
    }

    // Wait for the map style to be fully loaded
    if (!map.current.isStyleLoaded()) {
      map.current.once('style.load', () => {
        updateSetbacks();
      });
      return;
    }

    updateSetbacks();
  }, [showSetbacks, setbacks, parcelCoordinates]);

  const updateSetbacks = () => {
    // Remove existing setback lines
    const existingLayers = map.current.getStyle().layers;
    existingLayers.forEach(layer => {
      if (layer.id.startsWith('setback-')) {
        if (map.current.getLayer(layer.id)) {
          map.current.removeLayer(layer.id);
        }
        if (map.current.getSource(layer.id)) {
          map.current.removeSource(layer.id);
        }
      }
    });

    // Add new setback lines if enabled
    if (showSetbacks && setbacks) {
      setbacks.forEach((setback, index) => {
        const sourceId = `setback-${index}`;

        try {
          // Check if source already exists
          if (!map.current.getSource(sourceId)) {
            map.current.addSource(sourceId, {
              type: 'geojson',
              data: {
                type: 'Feature',
                properties: {},
                geometry: {
                  type: 'LineString',
                  coordinates: setback
                }
              }
            });
          }

          // Add the layer if it doesn't exist
          if (!map.current.getLayer(sourceId)) {
            map.current.addLayer({
              id: sourceId,
              type: 'line',
              source: sourceId,
              layout: {
                'line-join': 'round',
                'line-cap': 'round',
                'visibility': 'visible'
              },
              paint: {
                'line-color': '#FF0000',
                'line-width': 1,
                'line-dasharray': [2, 2]
              }
            });
          }
        } catch (error) {
          console.error('Error adding setback:', error);
        }
      });
    }
  };

  // Add parcel ID search function
  const searchByParcelId = async (id) => {
    if (!id.trim()) return;
    
    setParcelSearchLoading(true);
    try {
      // Example: Los Angeles County parcel lookup
      // Replace with your local government's API
      const response = await fetch(`https://portal.assessor.lacounty.gov/api/parcel/${id}`);
      
      if (response.ok) {
        const parcelData = await response.json();
        
        // If parcel found, add it to the map
        if (parcelData.geometry) {
          const feature = {
            type: 'Feature',
            geometry: parcelData.geometry,
            properties: {
              parcelId: id,
              address: parcelData.address,
              owner: parcelData.owner,
              landUse: parcelData.landUse,
              acreage: parcelData.acreage
            }
          };
          
          // Add to map and trigger parcel drawn callback
          if (draw.current) {
            draw.current.add(feature);
            handleDrawCreate({ features: [feature] });
          }
          
          setParcelInfo(parcelData);
          
          // Zoom to parcel
          if (map.current && parcelData.bbox) {
            map.current.fitBounds(parcelData.bbox, { padding: 50 });
          }
        }
      } else {
        alert('Parcel not found. Please check the ID and try again.');
      }
    } catch (error) {
      console.error('Error searching parcel:', error);
      alert('Error searching for parcel. Please try again.');
    } finally {
      setParcelSearchLoading(false);
    }
  };

  return (
    <div style={{ position: 'relative', width: '100%', height: '500px' }}>
      <div 
        ref={mapContainer} 
        style={{ width: '100%', height: '100%' }}
        tabIndex={0}
      />
      {showSetbacks && parcelCoordinates && <SetbackDialog />}
      <div style={{ 
        position: 'absolute', 
        top: '10px', 
        right: '50px', 
        display: 'flex', 
        gap: '8px' 
      }}>
        {/* Parcel ID Search */}
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Enter Parcel ID (APN)"
            value={parcelId}
            onChange={(e) => setParcelId(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && searchByParcelId(parcelId)}
            style={{
              padding: '8px 12px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '14px',
              width: '180px'
            }}
          />
          <button
            onClick={() => searchByParcelId(parcelId)}
            disabled={parcelSearchLoading}
            style={{
              padding: '8px 12px',
              backgroundColor: parcelSearchLoading ? '#ccc' : '#8B5CF6',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: parcelSearchLoading ? 'not-allowed' : 'pointer',
              fontSize: '14px'
            }}
          >
            {parcelSearchLoading ? 'Searching...' : 'Find Parcel'}
          </button>
        </div>
        
        {parcelCoordinates && (
          <>
            <button
              onClick={() => setShowSetbacks(!showSetbacks)}
              style={{
                padding: '8px 16px',
                backgroundColor: showSetbacks ? '#22C55E' : '#6B7280',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
            >
              {showSetbacks ? 'Hide Setbacks' : 'Show Setbacks'}
            </button>
            <button
              onClick={copyToVisualizer}
              style={{
                padding: '8px 16px',
                backgroundColor: copied ? '#22C55E' : '#3B82F6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
            >
              {copied ? 'Copied!' : 'Copy to Visualizer'}
            </button>
          </>
        )}
      </div>
      
      {/* Parcel Information Panel */}
      {parcelInfo && (
        <div style={{
          position: 'absolute',
          top: '60px',
          left: '10px',
          backgroundColor: 'white',
          padding: '16px',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          border: '1px solid #E5E7EB',
          minWidth: '250px',
          zIndex: 1000
        }}>
          <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600' }}>
            Parcel Information
          </h4>
          <div style={{ fontSize: '14px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div><strong>Parcel ID:</strong> {parcelInfo.parcelId || parcelId}</div>
            {parcelInfo.address && <div><strong>Address:</strong> {parcelInfo.address}</div>}
            {parcelInfo.owner && <div><strong>Owner:</strong> {parcelInfo.owner}</div>}
            {parcelInfo.landUse && <div><strong>Land Use:</strong> {parcelInfo.landUse}</div>}
            {parcelInfo.acreage && <div><strong>Acreage:</strong> {parcelInfo.acreage}</div>}
          </div>
          <button
            onClick={() => setParcelInfo(null)}
            style={{
              marginTop: '12px',
              padding: '6px 12px',
              backgroundColor: '#6B7280',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
};

export default MapboxParcelDrawer; 